import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "./config/api";

function AuthWrapper({ children }) {
  // "checking" until the bootstrap settles; it always ends in "ready" or "failed".
  const [status, setStatus] = useState("checking");
  const [attempt, setAttempt] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;

    const readStoredSession = () => {
      try {
        return JSON.parse(localStorage.getItem("sessionData"));
      } catch {
        return null; // corrupt storage is treated as "no session"
      }
    };

    const refreshUserModules = async (session) => {
      const response = await fetch(apiUrl("/user/modules"), {
        method: "GET",
        headers: {
          Authorization: session.sessionKey,
          Username: session.username,
        },
      });

      if (!response.ok) return false;

      const data = await response.json();
      if (!data.success || !Array.isArray(data.modules)) return false;

      localStorage.setItem("userModules", JSON.stringify({ modules: data.modules }));
      return true;
    };

    const refreshSession = async () => {
      const response = await fetch(apiUrl("/auth/refresh"), {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) return null;

      const result = await response.json();
      if (!result.success || !result.key) return null;

      const sessionData = {
        sessionKey: result.key,
        username: result.username,
        expiry: Date.now() + result.ttl,
        location: result.location,
      };
      localStorage.setItem("sessionData", JSON.stringify(sessionData));
      return sessionData;
    };

    const allowAccess = () => {
      if (active) setStatus("ready");
    };

    const sendToLogin = () => {
      try {
        localStorage.clear();
      } catch {
        // storage unavailable: nothing to clear
      }
      if (!active) return;
      setStatus("failed");
      navigate("/");
    };

    // Every step of the bootstrap (storage parsing, refresh, profile and
    // module reads) runs inside this function; any rejection settles the
    // screen through the catch below instead of leaving it on "Checking".
    const checkSession = async () => {
      let session = readStoredSession();

      if (!session || !session.sessionKey || !session.username) {
        session = await refreshSession();
      }

      if (session?.expiry && Date.now() > session.expiry) {
        session = await refreshSession();
      }

      if (!session || !session.sessionKey || !session.username) {
        sendToLogin();
        return;
      }

      const response = await fetch(apiUrl("/user/name"), {
        method: "GET",
        headers: {
          Authorization: session.sessionKey,
          Username: session.username,
        },
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("realName", data.name);
        await refreshUserModules(session);
        allowAccess();
      } else if (response.status === 401) {
        const refreshedSession = await refreshSession();
        if (refreshedSession) {
          await refreshUserModules(refreshedSession);
          allowAccess();
        } else {
          sendToLogin();
        }
      } else {
        sendToLogin();
      }
    };

    checkSession().catch((err) => {
      console.error("Session check failed:", err);
      sendToLogin();
    });

    return () => {
      active = false;
    };
  }, [navigate, attempt]);

  if (status === "checking") return <div>Checking session...</div>;
  if (status === "failed") {
    return (
      <div role="alert">
        <p>No se pudo verificar tu sesión.</p>
        <button type="button" onClick={() => { setStatus("checking"); setAttempt((value) => value + 1); }}>
          Reintentar
        </button>
        <button type="button" onClick={() => navigate("/")}>
          Ir a iniciar sesión
        </button>
      </div>
    );
  }
  return children;
}

export default AuthWrapper;
